import React, { useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSIconButton from "../../system-design/DSIconButton";
import DSChip from "../../system-design/DSChip";
import DSCard from "../../system-design/DSCard";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import { Close } from "@mui/icons-material";
import { formatDateTime } from "../../../utils/util";
import CalendarEventDialog from "./CalendarEventDialog";
import {
  CalendarEvent,
  DateTime,
  PopulatedCalendarEvent,
} from "../../../api/calendarEvent";
import { Klydo } from "../../../Types";
import {
  groupEventsByCountry,
  getPriorityForType,
  getPriorityColor,
  getPrioritizedEvents,
} from "../../../utils/calendarEventPriority";
import PriorityExplanationBox from "./PriorityExplanationBox";
import KlydoGridDisplay from "./KlydoGridDisplay";
import CountrySectionHeader from "./CountrySectionHeader";
import EventItemWithPriority from "./EventItemWithPriority";
import KlydoModal from "../../Klydos/components/KlydoModal";
import KlydoEventModal from "./KlydoEventModal";
import UnifiedCalendarEventModal from "./UnifiedCalendarEventModal";

type MultiHolidayDialogProps = {
  open: boolean;
  onClose: () => void;
  events: PopulatedCalendarEvent[];
  klydoEvents?: PopulatedCalendarEvent[];
  date: DateTime;
};

const MultiHolidayDialog = ({
  open,
  onClose,
  events,
  klydoEvents = [],
  date,
}: MultiHolidayDialogProps) => {
  const [selectedHoliday, setSelectedHoliday] =
    useState<PopulatedCalendarEvent | null>(null);
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | null>(null);
  const [klydoModalOpen, setKlydoModalOpen] = useState(false);
  const [editingKlydoEvent, setEditingKlydoEvent] =
    useState<CalendarEvent | null>(null);
  const [showCreateKlydoEvent, setShowCreateKlydoEvent] = useState(false);

  const formattedDate = formatDateTime(date);
  const eventsByCountry = groupEventsByCountry(events);
  const allKlydos = events.flatMap((event) => event.klydos || []);

  const handleHolidayClick = (calendarEvent: PopulatedCalendarEvent) => {
    setSelectedHoliday(calendarEvent);
  };

  const handleHolidayModalClose = () => {
    setSelectedHoliday(null);
  };

  const handleKlydoClick = (klydo: Klydo) => {
    setSelectedKlydo(klydo);
    setKlydoModalOpen(true);
  };

  const dateAsJsDate = new Date(date.year, date.month - 1, date.day);

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DSDialogTitle>
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DSTypography variant="h6">
            Event Details - {formattedDate}
          </DSTypography>
          <DSIconButton onClick={onClose}>
            <Close />
          </DSIconButton>
        </DSBox>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ p: 2 }}>
          {klydoEvents.length > 0 && (
            <>
              <DSBox sx={{ mb: 3 }}>
                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <DSTypography variant="h6">
                    Klydo Events ({klydoEvents.length})
                  </DSTypography>
                  <DSPrimaryButton
                    variant="outlined"
                    size="small"
                    onClick={() => setShowCreateKlydoEvent(true)}
                  >
                    + New Klydo Event
                  </DSPrimaryButton>
                </DSBox>
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {klydoEvents.map((evt) => (
                    <DSCard
                      key={evt.id}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => setEditingKlydoEvent(evt)}
                    >
                      <DSBox
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <DSBox>
                          <DSTypography variant="subtitle1" fontWeight="bold">
                            {evt.name || "Unnamed Event"}
                          </DSTypography>
                          <DSTypography variant="body2" color="text.secondary">
                            {evt.klydoIds?.length || 0} klydo
                            {(evt.klydoIds?.length || 0) !== 1 ? "s" : ""}
                          </DSTypography>
                        </DSBox>
                        <DSBox sx={{ display: "flex", gap: 0.5 }}>
                          {evt.overrideOtherEvents && (
                            <DSChip
                              label="Override"
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          )}
                        </DSBox>
                      </DSBox>
                    </DSCard>
                  ))}
                </DSBox>
              </DSBox>

              <DSBox
                sx={{
                  height: 1,
                  backgroundColor: "divider",
                  my: 2,
                }}
              />
            </>
          )}

          <DSBox sx={{ mb: 3 }}>
            <DSTypography variant="h6" sx={{ mb: 2 }}>
              Holidays ({events.length})
            </DSTypography>
            <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {events.map((calendarEvent, index) => (
                <DSBox
                  key={index}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <DSChip
                    label={calendarEvent.name}
                    variant="outlined"
                    onClick={() => handleHolidayClick(calendarEvent)}
                    sx={{
                      borderColor: "text.primary",
                      color: "text.primary",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  />
                  {calendarEvent.primaryType && (
                    <DSChip
                      label={calendarEvent.primaryType}
                      color={getPriorityColor(
                        getPriorityForType(calendarEvent.primaryType),
                      )}
                      size="small"
                      variant="filled"
                    />
                  )}
                </DSBox>
              ))}
            </DSBox>
          </DSBox>

          <PriorityExplanationBox />

          <DSBox
            sx={{
              height: 1,
              backgroundColor: "divider",
              my: 2,
            }}
          />

          <DSTypography variant="h6" sx={{ mb: 2 }}>
            Available Klydos ({allKlydos.length})
          </DSTypography>

          {allKlydos.length > 0 ? (
            <KlydoGridDisplay
              klydos={allKlydos}
              onKlydoClick={handleKlydoClick}
            />
          ) : (
            <DSTypography variant="body2" color="text.secondary">
              No klydos available for these calendar events.
            </DSTypography>
          )}

          <DSBox
            sx={{
              height: 1,
              backgroundColor: "divider",
              my: 3,
            }}
          />

          <DSTypography variant="h6" sx={{ mb: 2 }}>
            Events by Country
          </DSTypography>

          {Object.entries(eventsByCountry).map(
            ([countryName, countryEvents]) => {
              const prioritizedEvents = getPrioritizedEvents(countryEvents);
              const isMultiplePrioritized = prioritizedEvents.length > 1;

              return (
                <DSBox
                  key={countryName}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "white.5",
                  }}
                >
                  <CountrySectionHeader
                    countryName={countryName}
                    eventCount={countryEvents.length}
                    variant="h6"
                  />

                  {countryEvents.map((event) => {
                    const isPrioritized = prioritizedEvents.some(
                      (e) => e.id === event.id,
                    );

                    return (
                      <DSBox key={event.id} sx={{ mb: 2 }}>
                        <EventItemWithPriority
                          event={event}
                          isPrioritized={isPrioritized}
                          isMultiplePrioritized={isMultiplePrioritized}
                          variant="card"
                        />

                        {event.klydos && event.klydos.length > 0 && (
                          <DSBox sx={{ mt: 2, pl: 2 }}>
                            <DSTypography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {event.klydos.length} klydo
                              {event.klydos.length > 1 ? "s" : ""}
                            </DSTypography>
                            <KlydoGridDisplay
                              klydos={event.klydos.slice(0, 6)}
                              onKlydoClick={handleKlydoClick}
                            />
                            {event.klydos.length > 6 && (
                              <DSTypography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: "block" }}
                              >
                                +{event.klydos.length - 6} more klydos
                              </DSTypography>
                            )}
                          </DSBox>
                        )}
                      </DSBox>
                    );
                  })}
                </DSBox>
              );
            },
          )}
        </DSBox>
      </DSDialogContent>

      {selectedHoliday && (
        <CalendarEventDialog
          open={!!selectedHoliday}
          onClose={handleHolidayModalClose}
          calendarEvent={selectedHoliday}
        />
      )}

      {selectedKlydo && (
        <DSDialog
          open={klydoModalOpen}
          onClose={() => setKlydoModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={setKlydoModalOpen}
          />
        </DSDialog>
      )}

      {editingKlydoEvent && (
        <KlydoEventModal
          open={!!editingKlydoEvent}
          onClose={() => {
            setEditingKlydoEvent(null);
          }}
          event={editingKlydoEvent}
          date={dateAsJsDate}
        />
      )}

      {showCreateKlydoEvent && (
        <UnifiedCalendarEventModal
          open={showCreateKlydoEvent}
          onClose={() => setShowCreateKlydoEvent(false)}
          initialDate={dateAsJsDate}
          minDate={new Date()}
        />
      )}
    </DSDialog>
  );
};

export default MultiHolidayDialog;
